import {
  cn,
  healthOkFgClass,
  Label,
  statusOkDotClass,
  Switch,
} from '@up2stay/up2promo-ui';
import { Radio } from 'lucide-react';

import { listToolbarFilterButtonClass } from '@/core/components/layout/list-toolbar-styles';
import type { WorkspaceRealtimeConnectionState } from '@/core/hooks/use-workspace-realtime';

export type ProductsLiveScanBannerProps = {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  connectionState: WorkspaceRealtimeConnectionState;
  labels: {
    toggle: string;
    hint: string;
    statusConnecting: string;
    statusLive: string;
    statusError: string;
    statusOff: string;
  };
  className?: string;
};

function connectionLabel(
  state: WorkspaceRealtimeConnectionState,
  labels: ProductsLiveScanBannerProps['labels'],
): string {
  switch (state) {
    case 'connecting':
      return labels.statusConnecting;
    case 'open':
      return labels.statusLive;
    case 'error':
      return labels.statusError;
    case 'closed':
      return labels.statusConnecting;
    default:
      return labels.statusOff;
  }
}

/**
 * Compact toolbar control — Switch + fixed-footprint status signal.
 * Enabling must not grow the control (no conditional badge / text).
 */
export function ProductsLiveScanBanner({
  enabled,
  onEnabledChange,
  connectionState,
  labels,
  className,
}: ProductsLiveScanBannerProps) {
  const statusText = enabled
    ? connectionLabel(connectionState, labels)
    : labels.statusOff;
  const isLive = enabled && connectionState === 'open';
  const isError = enabled && connectionState === 'error';

  return (
    <div
      className={cn(
        listToolbarFilterButtonClass,
        'inline-flex h-10 min-h-10 shrink-0 items-center gap-2 px-3',
        className,
      )}
      data-guidance-target="live-scan"
      title={`${labels.hint} (${statusText})`}
    >
      <span className="relative inline-flex size-4 shrink-0 items-center justify-center">
        <Radio
          className={cn(
            'size-4 text-muted-foreground',
            enabled && !isError && healthOkFgClass,
            isError && 'text-destructive',
          )}
          aria-hidden
        />
        <span
          className={cn(
            'pointer-events-none absolute -right-0.5 -top-0.5 size-1.5 rounded-full bg-transparent',
            enabled && !isError && statusOkDotClass,
            isLive && 'animate-pulse motion-reduce:animate-none',
            isError && 'bg-destructive',
          )}
          aria-hidden
        />
      </span>
      <Label
        htmlFor="products-live-scan-toggle"
        className="cursor-pointer text-sm font-medium leading-none"
      >
        {labels.toggle}
      </Label>
      <span className="sr-only" aria-live="polite">
        {statusText}
      </span>
      <Switch
        id="products-live-scan-toggle"
        checked={enabled}
        onCheckedChange={onEnabledChange}
        className="shrink-0"
        aria-label={`${labels.toggle}. ${labels.hint}. ${statusText}`}
      />
    </div>
  );
}
